import { app } from "@microsoft/teams-js";

/**
 * Teams host lifecycle helpers (thin wrappers over @microsoft/teams-js `app`).
 *
 * `app.initialize()` completes the SDK handshake with the Teams host; it only
 * resolves inside a real Teams client (WebView2 / web / mobile), so it doubles
 * as host detection. `notifyTeamsAppReady()` tells the host the tab finished
 * loading so it can dismiss its loading indicator.
 */

/**
 * Initialize the Teams SDK. Resolves once the host handshake completes.
 * Rejects (or, outside a Teams host, never completes) when there is no host —
 * callers should treat a rejection as "not running in Teams".
 */
export async function initializeTeams(): Promise<void> {
  await app.initialize();
}

/**
 * Tell the host the bundle has loaded. Deliberately separate from
 * {@link notifyTeamsAppReady}: this only stops the host waiting on the document,
 * it does not claim the tab is usable.
 */
export function notifyTeamsAppLoaded(): void {
  app.notifyAppLoaded();
}

/**
 * Signal that the tab reached a usable state. Called after authentication, not
 * before: `notifySuccess` dismisses the host's loading indicator, and firing it
 * while the auth gate was still working is why a failing tab looked "loaded but
 * empty" rather than "still starting".
 */
export function notifyTeamsAppReady(): void {
  app.notifySuccess();
}

/**
 * The signed-in user's identity as the Teams host reports it, or `undefined`
 * outside a host / when the context is incomplete.
 *
 * Used to seed the account on silent token requests. MSAL's nested-app adapter
 * maps `account.homeAccountId` onto the broker's `platformBrokerId`; with no
 * account it sends none, and the Windows broker then has to resolve a *default*
 * account -- which is exactly what it refused with "The credential is invalid.
 * Default account not found." The NAA guidance says to supply the account from
 * the Teams context for this reason, noting that "the `homeAccountId` property is
 * equivalent to `userObjectId` in TeamsJS".
 */
export async function teamsUserIdentity(): Promise<
  { objectId: string; tenantId: string; username: string } | undefined
> {
  try {
    const context = await app.getContext();
    const objectId = context.user?.id;
    const tenantId = context.user?.tenant?.id;
    if (!objectId || !tenantId) return undefined;
    return {
      objectId,
      tenantId,
      username: context.user?.userPrincipalName ?? context.user?.loginHint ?? "",
    };
  } catch {
    return undefined;
  }
}
